import clsx from "clsx"
import React, { PropsWithChildren, ReactElement } from "react"
import { useInView } from "react-intersection-observer"
import { Dialog, DialogContent, DialogTitle, makeStyles, useMediaQuery, useTheme } from "@material-ui/core"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal"
import { SafeArea } from "../Skeleton/SafeArea"

type StyleProps = Readonly<{
    fullScreen: boolean
}>

const useStyles = makeStyles((theme) => ({
    // For wide screens we add blur to avoid distractions from dialog content
    dialogBackdrop: ({ fullScreen }: StyleProps) => ({
        backdropFilter: fullScreen ? undefined : "blur(10px)",
    }),
    // This ensures that contained elements with position: fixed will be positioned relative to the paper instead of viewport
    // Specifically, this prevents top bar and floating buttons from ending up outside the dialog when it is not full screen
    // See: https://developer.mozilla.org/en-US/docs/Web/CSS/position
    dialogPaper: ({ fullScreen }: StyleProps) => ({
        transform: fullScreen ? undefined : "scaleX(1)"
    }),
    dialogTitle: {
        padding: 0
    },
    dialogContent: {
        padding: 0
    },
    scrollShadowBottom: ({ fullScreen }: StyleProps) => ({
        "&::after": {
            zIndex: 1,
            position: fullScreen ? "fixed" : "sticky",
            content: '"-"',
            bottom: "-25px",
            left: "0",
            width: "100%",
            boxShadow: "0 -5px 45px rgb(0 0 0 / 80%)"
        }
    }),
    content: {
        height: "calc(100% - 10px)",
    },
    edge: {
        height: "10px"
    }
}))

type StandardDialogProps = Readonly<PropsWithChildren<{
    topbar: ReactElement
    open: boolean
    onClose: () => void
}>>

export function StandardDialog({ topbar, open, children, onClose }: StandardDialogProps) {
    const { ref: bottomRef, inView: isAtBottom } = useInView({ threshold: 1.0 })
    const theme = useTheme()
    const wide = useMediaQuery(theme.breakpoints.up("sm"))

    // If we are on a wide display (tablet or desktop or landscape phone) we never use full screen
    const fullScreen = !wide

    const classes = useStyles({ fullScreen })

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={(fullScreen ? undefined : "sm")}
            fullWidth={(fullScreen ? undefined : true)}
            fullScreen={fullScreen}
            scroll="paper"
            BackdropProps={{ classes: { root: classes.dialogBackdrop } }}
            classes={{
                paper: classes.dialogPaper,
                paperScrollPaper: clsx({
                    // TODO: We disable scroll shadow due to weird bug in Chrome
                    // with position:sticky causing the scroll to jump around
                    // Re-enable when we have time to debug the issue
                    [classes.scrollShadowBottom]: false  // !isAtBottom
                })
            }}
        >
            <ErrorHandlerModal close={onClose}>
                    <DialogTitle disableTypography className={classes.dialogTitle}>
                        {topbar}
                    </DialogTitle>
                    <DialogContent className={classes.dialogContent}>
                        <SafeArea fullHeight>
                            <div className={classes.content}>
                                {children}
                            </div>
                            <div ref={bottomRef} className={classes.edge}></div>
                        </SafeArea>
                    </DialogContent>
            </ErrorHandlerModal>
        </Dialog>
    )
}
